/**
 * Shared chart tooltip components for dashboard
 */

import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { formatCurrency } from '@/helpers/formatters';
import { MAX_HOTEL_NAME_CHARACTERS } from '@/constants';

// Price Tooltip for charts
export const PriceTooltip = ({ active, payload, label, currency }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border-2 border-primary/20 rounded-lg shadow-xl p-4 backdrop-blur-sm">
        <p className="font-semibold text-foreground mb-3 text-sm">{label}</p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: entry.color }} />
                <span className="text-muted-foreground">
                  {entry?.name?.length > MAX_HOTEL_NAME_CHARACTERS ? `${entry?.name?.slice(0, MAX_HOTEL_NAME_CHARACTERS)}...` : entry?.name}
                </span>
              </div>
              <span className="font-bold text-foreground">{formatCurrency(entry?.value, currency)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return null;
};

// Scatter Tooltip for price vs rating charts
export const ScatterTooltip = ({ active, payload, currency }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    const isYourHotel = d.isYourHotel;
    return (
      <div className="bg-card border-2 border-primary/20 rounded-lg shadow-xl p-4 backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-3">
          <p className="font-semibold text-foreground text-sm">
            {d?.name?.length > MAX_HOTEL_NAME_CHARACTERS ? `${d?.name?.slice(0, MAX_HOTEL_NAME_CHARACTERS)}...` : d?.name}
          </p>
          {isYourHotel && <Badge variant="default" className="text-xs">You</Badge>}
        </div>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Rating:</span>
            <span className="font-bold text-foreground">{d?.rating?.toFixed(1)} ⭐</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Price:</span>
            <span className="font-bold text-foreground">{formatCurrency(d?.lowest_price, currency)}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Reviews:</span>
            <span className="font-bold text-foreground">{d?.reviews?.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// OTA Performance Tooltip
export const OTATooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-card border-2 border-primary/20 rounded-lg shadow-xl p-4 backdrop-blur-sm">
        <p className="font-semibold text-foreground mb-3 text-sm">{d.ota}</p>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Average:</span>
            <span className="font-bold text-foreground">₹{d.avg_price?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Minimum:</span>
            <span className="font-medium text-emerald-500">₹{d.min_price?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Maximum:</span>
            <span className="font-medium text-red-500">₹{d.max_price?.toLocaleString()}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Market Share Tooltip
export const MarketShareTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div className="bg-card border-2 border-primary/20 rounded-lg shadow-xl p-4 backdrop-blur-sm">
        <p className="font-semibold text-foreground mb-3 text-sm">{d.name}</p>
        <div className="space-y-1.5 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Reviews:</span>
            <span className="font-bold text-foreground">{d.reviews?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-muted-foreground">Market Share:</span>
            <span className="font-bold text-primary">{d.share}%</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Demand Tooltip for pricing opportunity charts
export const DemandTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const demandData = payload.find(p => p.dataKey === 'demand');
    const availableData = payload.find(p => p.dataKey === 'available');

    return (
      <div className="bg-card border-2 border-primary/20 rounded-lg shadow-xl p-4 backdrop-blur-sm">
        <p className="font-semibold text-foreground mb-3 text-sm">{label}</p>
        <div className="space-y-2">
          {payload.map((entry, index) => (
            <div key={index} className="flex items-center justify-between gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full shadow-sm" style={{ backgroundColor: entry.color }} />
                <span className="text-muted-foreground">{entry.name}:</span>
              </div>
              <span className="font-bold text-foreground">
                {entry.dataKey === 'demand' ? `${entry.value}%` : `₹${entry.value?.toLocaleString()}`}
              </span>
            </div>
          ))}
        </div>
        {demandData && (
          <div className="mt-3 pt-3 border-t border-border">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {demandData.value > 85 ? (
                <Badge variant="destructive" className="text-xs">High Demand</Badge>
              ) : demandData.value > 70 ? (
                <Badge variant="default" className="text-xs">Moderate Demand</Badge>
              ) : (
                <Badge variant="secondary" className="text-xs">Low Demand</Badge>
              )}
            </div>
          </div>
        )}\n      </div>
    );
  }
  return null;
};

export const AmenitieBarTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  return (
    <div className="bg-card border border-border rounded-xl shadow-lg px-4 py-3 min-w-[180px]">

      {/* Label (X axis value) */}
      <p className="text-sm font-semibold mb-2 text-foreground">
        {label}
      </p>

      {/* Bars */}
      <div className="space-y-1">
        {payload.map((entry, index) => (
          <div
            key={index}
            className="flex items-center justify-between text-sm"
          >
            <div className="flex items-center gap-2">
              {/* Color indicator */}
              <div
                className="w-3 h-3 rounded-sm"
                style={{ backgroundColor: entry.color }}
              />
              <span className="text-muted-foreground">
                {entry?.name?.length > MAX_HOTEL_NAME_CHARACTERS ? `${entry?.name?.slice(0, MAX_HOTEL_NAME_CHARACTERS)}...` : entry?.name}
              </span>
            </div>

            <span className="font-medium ml-3 text-foreground">
              {`${entry.value} %`}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const AmenitieRadarTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;

  // Sort so "Your Hotel" appears first
  const sortedPayload = [...payload].sort((a, b) => {
    if (a.name === "Your Hotel") return -1;
    if (b.name === "Your Hotel") return 1;
    return 0;
  });

  return (
    <div className="bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-xl px-4 py-3 min-w-[200px]">

      {/* Category Title */}
      <p className="text-sm font-semibold text-foreground mb-2">
        {label}
      </p>

      <div className="space-y-1.5">
        {sortedPayload.map((entry, index) => (
          <div
            key={index}
            className="flex items-center justify-between text-sm"
          >
            <div className="flex items-center gap-2">
              {/* Color Dot */}
              <div
                className="w-3 h-3 rounded-full"
                style={{ backgroundColor: entry.color }}
              />

              <span
                className={
                  entry.name === "Your Hotel"
                    ? "font-semibold text-foreground"
                    : "text-muted-foreground"
                }
              >
                {entry?.name?.length > MAX_HOTEL_NAME_CHARACTERS ? `${entry?.name?.slice(0, MAX_HOTEL_NAME_CHARACTERS)}...` : entry?.name}
              </span>
            </div>

            <span className="font-medium ml-2 text-foreground">
              {`${entry.value} % `}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const SmartRateTooltip = ({ active, payload, label, currency }) => {
  const [showAll, setShowAll] = useState(false);
  const MAX_VISIBLE = 8; // Show max 8 OTAs initially


  if (!active || !payload || !payload.length) return null;

  // Sort by value (price) descending
  const sortedPayload = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));


  const directEntry = sortedPayload.find(p => p.name?.includes('Direct'));
  const otaEntries = sortedPayload.filter(p => !p.name?.includes('Direct'));

  // Calculate stats
  const directRate = directEntry?.value || 0;
  const avgOtaRate = otaEntries.length > 0
    ? Math.round(otaEntries.reduce((sum, p) => sum + (p.value || 0), 0) / otaEntries.length)
    : 0;
  const lowestOta = otaEntries.length > 0 ? otaEntries[otaEntries.length - 1] : null;
  const highestOta = otaEntries.length > 0 ? otaEntries[0] : null;

  const visibleOtas = showAll ? otaEntries : otaEntries.slice(0, MAX_VISIBLE);
  const hiddenCount = otaEntries.length - MAX_VISIBLE;

  return (
    <div className="bg-card border-2 border-primary/20 rounded-lg shadow-2xl backdrop-blur-sm max-w-[320px]">
      {/* Header */}
      <div className="px-4 py-2 border-b border-border bg-muted/50 rounded-t-lg">
        <p className="font-semibold text-foreground text-sm">{label}</p>
        <p className="text-xs text-muted-foreground">{otaEntries.length + 1} OTAs tracked</p>
      </div>

      {/* Summary Stats */}
      <div className="px-4 py-2 border-b border-border grid grid-cols-2 gap-2 text-xs">
        {directEntry && (
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500" />
            <span className="text-muted-foreground">Direct:</span>
            <span className="font-bold text-blue-600">{formatCurrency(directRate, currency)}</span>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Avg OTA:</span>
          <span className="font-bold">{formatCurrency(avgOtaRate, currency)}</span>
        </div>
        {lowestOta && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Lowest:</span>
            <span className="font-bold text-red-500">{formatCurrency(lowestOta.value, currency)}</span>
          </div>
        )}
        {highestOta && (
          <div className="flex items-center gap-2">
            <span className="text-muted-foreground">Highest:</span>
            <span className="font-bold text-emerald-500">{formatCurrency(highestOta.value, currency)}</span>
          </div>
        )}
      </div>

      {/* OTA List - Scrollable */}
      <div className={`px-4 py-2 space-y-1 ${showAll && otaEntries.length > 10 ? 'max-h-[200px] overflow-y-auto' : ''}`}>
        {/* Direct first */}
        {directEntry && (
          <div className="flex items-center justify-between text-sm py-1 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: directEntry.color }} />
              <span className="font-medium text-foreground">{directEntry.name}</span>
            </div>
            <span className="font-bold text-blue-600">{formatCurrency(directEntry.value, currency)}</span>
          </div>
        )}

        {/* OTAs */}
        {visibleOtas.map((entry, index) => {
          const diff = directRate > 0 ? ((entry.value - directRate) / directRate * 100).toFixed(1) : 0;
          const isLower = entry.value < directRate * 0.98;
          const isHigher = entry.value > directRate * 1.02;

          return (
            <div key={index} className="flex items-center justify-between text-xs py-0.5">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: entry.color }} />
                <span className="text-muted-foreground truncate max-w-[120px]" title={entry.name}>{entry.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-foreground">{formatCurrency(entry.value, currency)}</span>
                {/* <span className={`text-[10px] w-12 text-right ${isLower ? 'text-red-500' : isHigher ? 'text-emerald-500' : 'text-muted-foreground'}`}>
                  {diff > 0 ? '+' : ''}{diff}%
                </span> */}
              </div>
            </div>
          );
        })}

        {/* Show more/less button */}
        {hiddenCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowAll(!showAll);
            }}
            className="w-full text-xs text-primary hover:text-primary/80 py-1.5 text-center font-medium"
          >
            {showAll ? 'Show less' : `${hiddenCount} more OTAs`}
          </button>
        )}
      </div>
    </div>
  );
};



//AmenityHatmap
export const getHeatmapColor = (value) => {
  if (value === null || value === undefined) return "bg-muted/30";
  if (value >= 90) return "bg-emerald-500";
  if (value >= 80) return "bg-emerald-400";
  if (value >= 70) return "bg-yellow-400";
  if (value >= 60) return "bg-orange-400";
  if (value >= 50) return "bg-orange-500";
  return "bg-red-500";
};



export const AmenityHeatmapTooltip = ({
  hotel,
  amenity,
  value,
  isYourHotel,
  rowIndex
}) => {
  if (value === null || value === undefined) return null;

  const isFirstRow = rowIndex === 0;

  return (
    <div
      className={`absolute z-[9999] bg-card border border-border rounded-lg shadow-xl p-3 min-w-[180px] pointer-events-none
      left-1/2 -translate-x-1/2
      ${isFirstRow ? "top-full mt-2" : "bottom-full mb-2"}`}
    >
      {/* Arrow */}
      <div
        className={`absolute left-1/2 transform -translate-x-1/2
        ${isFirstRow ? "top-0 -translate-y-full" : "bottom-0 translate-y-full"}`}
      >
        <div
          className={`w-0 h-0 border-l-8 border-r-8 border-l-transparent border-r-transparent
          ${isFirstRow ? "border-b-8 border-b-border" : "border-t-8 border-t-border"}`}
        />
      </div>

      <p className="font-semibold text-foreground text-sm mb-1">
        {hotel} {isYourHotel && <span className="text-primary">(You)</span>}
      </p>

      <p className="text-muted-foreground text-xs mb-2">{amenity}</p>

      <div className="flex items-center gap-2">
        <div className={`w-3 h-3 rounded ${getHeatmapColor(value)}`} />
        <span className="font-bold text-foreground">{value}%</span>
        <span className="text-xs text-muted-foreground">
          positive sentiment
        </span>
      </div>
    </div>
  );
};